// src/app/core/shared/cost-metrics.snapshot.ts

// A compact, per-year snapshot of key KPIs and costs (single-year view)
export interface CostMetricsSnapshot {
    year: string;

    // Derived from request Costmetrics -> Health Sector Cost -> Discount rate
    discountRate: number;
    discountSuffix: 'three' | 'five' | 'ten' | 'fifteen';

    // KPIs (from htn)
    diagnosed?: number;
    treated?: number;
    controlled?: number;
    cvdPatients?: number;
    cvdEvents?: number;

    // Costs (from cm)
    primaryCareCost?: number;               // cumulative_primary_care_cost_current_*
    cvdEventCost?: number;                  // cumulative_CVD_event_cost_current_*
    healthSectorCost?: number;              // cummulative_health_sector_cost_current_*
    socialCost?: number;                    // cumulative_social_cost_current_*
    interventionRunningCost?: number;       // total_running_cost_10_interventions_current_*
    interventionInvestmentCost?: number;    // total_investment_cost_10_interventions_current_*
    monetizedQaly?: number;                 // cummulative_monetized_QALY_gains_current_*
    valuedEachQaly?: number;                // valued_each_QALY (not suffixed)
    netBenefitHealthSector?: number;        // net_benefit_health_sector_current_*
    netBenefitHealthAndSocial?: number;     // net_benefit_health_and_social_sector_current_*
}

// Full all-years series (HTN-like) for selected cost metrics and KPIs
export interface CostMetricsSeries {
    discountRate: number;
    discountSuffix: 'three' | 'five' | 'ten' | 'fifteen';
    years: string[];
    // Keys are the exact metric names (e.g., 'cumulative_primary_care_cost_current_three', 'Diagnosed')
    series: Record<string, Record<string, number>>;
}

// ------------- internal helpers -------------

function toNum(v: unknown): number | undefined {
    if (v == null) return undefined;
    if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
    if (typeof v === 'string') {
        const n = parseFloat(v.replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
}

// Detect if cm is year-first (cm["2045"]={...}) or metric-first (cm["metric"]={"2045":...})
function isYearFirstCm(cm: any): boolean {
    if (!cm || typeof cm !== 'object') return false;
    return Object.keys(cm).some(k => /^\d{4}$/.test(k));
}

// Map discount rate to the suffix used by your cm series keys
function toDiscountSuffix(rate: number | undefined | null): 'three' | 'five' | 'ten' | 'fifteen' {
    const r = Number(rate ?? 0.03);
    if (Math.abs(r - 0.03) < 1e-6) return 'three';
    if (Math.abs(r - 0.05) < 1e-6) return 'five';
    if (Math.abs(r - 0.10) < 1e-6) return 'ten';
    if (Math.abs(r - 0.15) < 1e-6) return 'fifteen';
    return 'three';
}

function pickYear(years: string[] | undefined | null, uiYear?: string | number): string {
    if (uiYear != null) return String(uiYear);
    const ys = Array.isArray(years) ? years.map(String) : [];
    if (ys.includes('2045')) return '2045';
    if (ys.length) return ys[ys.length - 1];
    return '2045';
}

// Read a metric from cm for a given year, supporting both orientations and multiple key candidates
function readCmMetric(
    cm: any,
    metricKeys: string[],
    year: string
): number | undefined {
    if (!cm) return undefined;
    const yearFirst = isYearFirstCm(cm);

    for (const mk of metricKeys) {
        if (yearFirst) {
            const raw = cm?.[year]?.[mk];
            const n = toNum(raw);
            if (n !== undefined) return n;
        } else {
            const raw = cm?.[mk]?.[year];
            const n = toNum(raw);
            if (n !== undefined) return n;
        }
    }
    return undefined;
}

// Read a KPI from htn for a given year
function readHtnMetric(
    htn: any,
    metricKeys: string[],
    year: string
): number | undefined {
    if (!htn) return undefined;
    for (const mk of metricKeys) {
        const raw = htn?.[mk]?.[year];
        const n = toNum(raw);
        if (n !== undefined) return n;
    }
    return undefined;
}

// Extract Discount rate from the request payload (if available)
function extractDiscountRateFromRequest(req: any): number {
    try {
        const rate = req?.Costmetrics?.['Health Sector Cost']?.['Discount rate'];
        const n = Number(rate);
        return Number.isFinite(n) ? n : 0.03;
    } catch { return 0.03; }
}

// Build a full-year series from cm for a given metric name
function readCmSeries(cm: any, metricKey: string, years: string[]): Record<string, number> {
    const out: Record<string, number> = {};
    const yearFirst = isYearFirstCm(cm);
    for (const y of years) {
        let raw: unknown;
        if (yearFirst) raw = cm?.[y]?.[metricKey];
        else raw = cm?.[metricKey]?.[y];
        const n = toNum(raw);
        if (n !== undefined) out[y] = n;
    }
    return out;
}

// Build a full-year series from htn for a chosen metric (first available candidate)
function readHtnSeries(htn: any, metricCandidates: string[], years: string[]): { key: string; series: Record<string, number> } | null {
    if (!htn) return null;
    const mk = metricCandidates.find(k => htn?.[k] && typeof htn[k] === 'object');
    if (!mk) return null;
    const out: Record<string, number> = {};
    for (const y of years) {
        const n = toNum(htn?.[mk]?.[y]);
        if (n !== undefined) out[y] = n;
    }
    return { key: mk, series: out };
}

// ------------- main builders (single-year) -------------

export function buildCostMetricsSnapshot(
    cm: any,
    htn: any,
    year: string | number,
    discountRate?: number
): CostMetricsSnapshot | null {
    if (!cm) return null;

    const y = String(year);
    const suffix = toDiscountSuffix(discountRate);

    // Costs (metric names matched to your API)
    const primaryCareCost = readCmMetric(cm, [`cumulative_primary_care_cost_current_${suffix}`], y);
    const cvdEventCost = readCmMetric(cm, [`cumulative_CVD_event_cost_current_${suffix}`], y);
    const healthSectorCost = readCmMetric(cm, [`cummulative_health_sector_cost_current_${suffix}`], y); // double 'm' in cummulative
    const socialCost = readCmMetric(cm, [`cumulative_social_cost_current_${suffix}`], y);
    const interventionRunningCost = readCmMetric(cm, [`total_running_cost_10_interventions_current_${suffix}`], y);
    const interventionInvestmentCost = readCmMetric(cm, [`total_investment_cost_10_interventions_current_${suffix}`], y);
    const monetizedQaly = readCmMetric(cm, [`cummulative_monetized_QALY_gains_current_${suffix}`], y); // double 'm'
    const valuedEachQaly = readCmMetric(cm, ['valued_each_QALY'], y);

    const netBenefitHealthAndSocial = readCmMetric(cm, [`net_benefit_health_and_social_sector_current_${suffix}`], y);
    const netBenefitHealthSector = readCmMetric(cm, [`net_benefit_health_sector_current_${suffix}`], y);

    // KPIs from htn
    const diagnosed = readHtnMetric(htn, ['Diagnosed'], y);
    const treated = readHtnMetric(htn, ['Treated'], y);
    const controlled = readHtnMetric(htn, ['Controlled'], y);
    const cvdPatients = readHtnMetric(htn, ['CVD_Patients', 'CVD_patients'], y);
    const cvdEvents = readHtnMetric(htn, ['CVD_Events', 'CVD_events'], y);

    return {
        year: y,
        discountRate: Number(discountRate ?? 0.03),
        discountSuffix: suffix,
        diagnosed,
        treated,
        controlled,
        cvdPatients,
        cvdEvents,
        primaryCareCost,
        cvdEventCost,
        healthSectorCost,
        socialCost,
        interventionRunningCost,
        interventionInvestmentCost,
        monetizedQaly,
        valuedEachQaly,
        netBenefitHealthAndSocial,
        netBenefitHealthSector
    };
}

export function buildCostMetricsSnapshotFromResp(
    resp: any,
    options?: { request?: any; uiYear?: string | number }
): CostMetricsSnapshot | null {
    const cm =
        resp?.cm ??
        resp?.payload?.[0]?.response?.cm ??
        resp?.payload?.[0]?.response?.costCm ??
        resp?.costCm ??
        null;

    const htn = resp?.htn ?? null;
    if (!cm) return null;

    const uiYear = options?.uiYear;
    const year = pickYear(resp?.years, uiYear);
    const discountRate = extractDiscountRateFromRequest(options?.request);

    return buildCostMetricsSnapshot(cm, htn, year, discountRate);
}

// ------------- main builders (all-years) -------------

export function buildCostMetricsSeries(
    cm: any,
    htn: any,
    years: string[],
    discountRate?: number
): CostMetricsSeries | null {
    if (!cm || !Array.isArray(years) || !years.length) return null;

    const suffix = toDiscountSuffix(discountRate);
    const series: Record<string, Record<string, number>> = {};

    // Costs (exact metric names as in your API)
    const costMetricKeys = [
        `cumulative_primary_care_cost_current_${suffix}`,
        `cumulative_CVD_event_cost_current_${suffix}`,
        `cummulative_health_sector_cost_current_${suffix}`,
        `cumulative_social_cost_current_${suffix}`,
        `total_running_cost_10_interventions_current_${suffix}`,
        `total_investment_cost_10_interventions_current_${suffix}`,
        `cummulative_monetized_QALY_gains_current_${suffix}`,
        `net_benefit_health_sector_current_${suffix}`,
        `net_benefit_health_and_social_sector_current_${suffix}`,
        'valued_each_QALY'
    ];

    for (const mk of costMetricKeys) {
        const s = readCmSeries(cm, mk, years);
        if (Object.keys(s).length) series[mk] = s;
    }

    // KPIs from htn (use the canonical keys present in your response)
    const kpiDefs: Array<{ name: string; candidates: string[] }> = [
        { name: 'Diagnosed', candidates: ['Diagnosed'] },
        { name: 'Treated', candidates: ['Treated'] },
        { name: 'Controlled', candidates: ['Controlled'] },
        { name: 'CVD_Patients', candidates: ['CVD_Patients', 'CVD_patients'] },
        { name: 'CVD_Events', candidates: ['CVD_Events', 'CVD_events'] }
    ];
    for (const def of kpiDefs) {
        const res = readHtnSeries(htn, def.candidates, years);
        if (res && Object.keys(res.series).length) {
            // Use the canonical name (e.g., 'CVD_Patients'), not the candidate key casing
            series[def.name] = res.series;
        }
    }

    return {
        discountRate: Number(discountRate ?? 0.03),
        discountSuffix: suffix,
        years: years.map(String),
        series
    };
}

export function buildCostMetricsSeriesFromResp(
    resp: any,
    options?: { request?: any }
): CostMetricsSeries | null {
    const cm =
        resp?.cm ??
        resp?.payload?.[0]?.response?.cm ??
        resp?.payload?.[0]?.response?.costCm ??
        resp?.costCm ??
        null;

    const htn = resp?.htn ?? null;
    const years: string[] = Array.isArray(resp?.years) ? resp.years.map(String) : [];
    if (!cm || !years.length) return null;

    const discountRate = extractDiscountRateFromRequest(options?.request);
    return buildCostMetricsSeries(cm, htn, years, discountRate);
}